import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/checkins";
import { DiaryChangeTable } from "../staff/DiaryChangeTable";
import { DiaryPdfButtons } from "../staff/DiaryPdfButtons";
import { DiaryTrendChart } from "../staff/DiaryTrendChart";
import { errMsg } from "../staff/ResolveDialog";

const period = (p) => `${fmtDate(p?.start)} – ${fmtDate(p?.end)}`;

function Pick({ value, onChange, rows, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-80" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {rows.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`${testId}-option-${r.id}`}>{period(r.period)} · {r.entries} entries</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function WeekColumn({ tag, s }) {
  const m = s.summary;
  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 p-3" data-testid={`diary-compare-${tag.toLowerCase()}`}>
      <div>
        <div className="font-display text-base font-semibold text-zinc-950">{tag}: {period(s.period)}</div>
        <div className="text-xs text-zinc-600">{m.entries} entries on {m.days_logged} of {m.days_total} days · v{s.semantic_version}</div>
      </div>
      <DiaryTrendChart config={s.diary_config} diary={s.diary} />
      <div className="flex flex-wrap gap-1.5">
        {s.flags.length ? s.flags.map((f) => <span key={f.code} className="rounded bg-amber-100 px-1.5 py-0.5 text-2xs font-semibold text-amber-900">{f.label}</span>)
          : <span className="text-xs text-zinc-500">No flags</span>}
      </div>
      {s.review && <p className="text-xs text-zinc-700"><b>Review:</b> {s.review.interpretation}</p>}
    </div>
  );
}

/** Two finished diaries side by side (earlier on the left) with a change summary and a printable page. */
export function DiaryCompareDialog({ rows, onClose }) {
  const [a, setA] = useState(rows[1].id);
  const [b, setB] = useState(rows[0].id);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null);
    setErr(null);
    if (a === b) { setErr("Pick two different diaries."); return; }
    api.get("/checkin-diaries/compare", { params: { a, b } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [a, b]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto" data-testid="diary-compare-dialog">
        <DialogHeader>
          <DialogTitle>Week-to-week comparison</DialogTitle>
          <DialogDescription>Pick any two diaries — the earlier week is always shown on the left. Both charts use the same 0–10 scale.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Pick value={a} onChange={setA} rows={rows} testId="diary-compare-a" />
          <span className="text-sm text-zinc-500">compared with</span>
          <Pick value={b} onChange={setB} rows={rows} testId="diary-compare-b" />
          {d && (
            <div className="ml-auto">
              <DiaryPdfButtons url="/checkin-diaries/compare-pdf" params={{ a, b }} label="Print comparison" testPrefix="diary-compare-pdf"
                               filename={`diary-comparison-${(d.client_name || "client").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`} />
            </div>
          )}
        </div>
        {err && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="diary-compare-error">{err}</p>}
        {!d && !err && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
        {d && (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <WeekColumn tag="Earlier" s={d.earlier} />
              <WeekColumn tag="Later" s={d.later} />
            </div>
            <DiaryChangeTable rows={d.changes} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
